import React, { useCallback, useEffect, useRef, useState } from 'react';
import connectUser from '../connectUser';
import Icon from '../ui/Icon';
import AppIcon from '../ui/AppIcon';
import { appById } from '../registry/appRegistry';
import alertsApi, { notifyAlertsChanged } from '../alertsApi';
import accessApi from '../accessApi';
import './screens.css';

// How often the feed re-polls so new notifications (a fresh access request for
// an admin, a grant/deny outcome for a user, a workspace invite) appear without
// a manual reload. No websocket push is enabled, so this interval is it.
const POLL_MS = 30000;

// Map an alert's classification to a lucide icon + accent color. Uses the
// classic lucide names (AlertTriangle/AlertCircle) that this older bundled
// lucide-react ships — the newer TriangleAlert/CircleAlert aliases render as a
// blank square here.
function alertVisual(classification) {
  switch (classification) {
    case 'warning':
      return { icon: 'AlertTriangle', color: '#e0a53b' };
    case 'critical':
      return { icon: 'AlertCircle', color: '#d9534f' };
    default:
      return { icon: 'Info', color: '#3f9c6d' };
  }
}

function reqName(r) {
  return [r.firstname, r.lastname].filter(Boolean).join(' ') || r.username || 'Someone';
}

function UpdatesScreen({ userInfo }) {
  const isMaster = (userInfo?.routes ? [...userInfo.routes] : []).includes('app-control');

  const [alerts, setAlerts] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [clearing, setClearing] = useState(false);
  const mounted = useRef(true);

  const load = useCallback(
    (showSpinner) => {
      if (showSpinner) setLoading(true);
      // Master-admins also see the authoritative pending-request queue (the
      // source of truth, moved here from App Control). Everyone gets alerts.
      const calls = [alertsApi.list()];
      if (isMaster) calls.push(accessApi.listRequests());
      Promise.all(calls)
        .then(([aRes, rRes]) => {
          if (!mounted.current) return;
          setAlerts(Array.isArray(aRes?.data) ? aRes.data : []);
          if (isMaster) setRequests(Array.isArray(rRes?.data) ? rRes.data : []);
        })
        .catch(() => {})
        .finally(() => {
          if (mounted.current) setLoading(false);
        });
    },
    [isMaster]
  );

  useEffect(() => {
    mounted.current = true;
    load(true);
    const t = setInterval(() => load(false), POLL_MS);
    return () => {
      mounted.current = false;
      clearInterval(t);
    };
  }, [load]);

  // The pending-request queue is authoritative, so the ACCESS_REQUEST alert
  // copies would just duplicate it — hide them from the notifications list.
  const notifications = alerts.filter((a) => a.type !== 'ACCESS_REQUEST');

  const dismiss = (alertId) => {
    if (!alertId) return;
    setBusyId(alertId);
    alertsApi
      .resolve(alertId)
      .then(() => {
        setAlerts((list) => list.filter((a) => a.alertId !== alertId));
        notifyAlertsChanged();
      })
      .catch(() => {})
      .finally(() => {
        setBusyId(null);
        load(false);
      });
  };

  const clearAll = () => {
    if (!notifications.length || clearing) return;
    setClearing(true);
    // Only clears dismissible notifications — never the pending-request queue.
    const ids = notifications.map((a) => a.alertId).filter(Boolean);
    Promise.all(ids.map((id) => alertsApi.resolve(id).catch(() => {})))
      .then(() => {
        if (mounted.current) setAlerts((list) => list.filter((a) => a.type === 'ACCESS_REQUEST'));
        notifyAlertsChanged();
      })
      .finally(() => {
        if (mounted.current) setClearing(false);
        load(false);
      });
  };

  const respondInvite = (spaceId, accept) => {
    if (!spaceId) return;
    setBusyId(spaceId);
    const call = accept ? alertsApi.acceptInvite(spaceId) : alertsApi.declineInvite(spaceId);
    call
      .then((res) => {
        if (res?.data?.success === false) return;
        load(false);
        notifyAlertsChanged();
      })
      .catch(() => {})
      .finally(() => setBusyId(null));
  };

  // Approve / deny a pending access request. Also clears its ACCESS_REQUEST
  // alert copies locally so the bell badge drops immediately even if the server
  // hasn't been restarted with the clearAccessRequest cleanup yet.
  const clearRequestAlerts = (requestId) => {
    const stale = alerts.filter((a) => a.identifier && a.identifier.requestId === requestId);
    stale.forEach((a) => alertsApi.resolve(a.alertId).catch(() => {}));
    if (stale.length) {
      setAlerts((list) => list.filter((a) => !(a.identifier && a.identifier.requestId === requestId)));
    }
  };

  const respondRequest = (req, approve) => {
    setBusyId(req.requestId);
    const call = approve ? accessApi.grant(req.userId, req.appId) : accessApi.deny(req.requestId);
    call
      .then((res) => {
        if (res?.data?.success === false) return;
        setRequests((list) => list.filter((r) => r.requestId !== req.requestId));
        clearRequestAlerts(req.requestId);
        notifyAlertsChanged();
      })
      .catch(() => {})
      .finally(() => {
        setBusyId(null);
        load(false);
      });
  };

  const nothing = !loading && requests.length === 0 && notifications.length === 0;

  return (
    <div className="hive-container hive-screen">
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          marginBottom: 20,
        }}
      >
        <h1 className="hive-h2 hive-screen-h" style={{ marginBottom: 0 }}>Updates</h1>
        {notifications.length > 0 && (
          <button
            type="button"
            className="hive-invite-decline hive-tap"
            disabled={clearing}
            onClick={clearAll}
          >
            {clearing ? 'Clearing…' : 'Clear all'}
          </button>
        )}
      </div>

      {loading ? (
        <div className="hive-empty"><p>Loading…</p></div>
      ) : nothing ? (
        <div className="hive-empty hive-empty-lg">
          <div className="hive-empty-ic"><Icon name="BellRing" size={30} /></div>
          <p className="hive-empty-title">You’re all caught up</p>
          <p>App updates and announcements will show up here.</p>
        </div>
      ) : (
        <>
          {/* Pending access requests (master-admin only) — authoritative queue. */}
          {isMaster && requests.length > 0 && (
            <section style={{ marginBottom: 28 }}>
              <h2 className="hive-section-title" style={{ marginBottom: 12 }}>
                Pending requests <span className="hive-ac-count">{requests.length}</span>
              </h2>
              <div className="hive-invite-list">
                {requests.map((r) => {
                  const app = appById(r.appId);
                  const busy = busyId === r.requestId;
                  return (
                    <div key={r.requestId} className="hive-invite-card">
                      {app ? (
                        <AppIcon app={app} size={40} />
                      ) : (
                        <span className="hive-space-ic" style={{ background: '#e0a53b' }}>
                          <Icon name="AlertTriangle" size={18} color="#fff" />
                        </span>
                      )}
                      <div className="hive-invite-body">
                        <span className="hive-card-title">{reqName(r)}</span>
                        <span className="hive-meta">wants {app ? app.name : r.appId}</span>
                      </div>
                      <div className="hive-invite-actions">
                        <button
                          type="button"
                          className="hive-invite-accept hive-tap"
                          disabled={busy}
                          onClick={() => respondRequest(r, true)}
                        >
                          Approve
                        </button>
                        <button
                          type="button"
                          className="hive-invite-decline hive-tap"
                          disabled={busy}
                          onClick={() => respondRequest(r, false)}
                        >
                          Deny
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {/* Notifications (everything except the request copies). */}
          {notifications.length > 0 && (
            <section>
              {isMaster && requests.length > 0 && (
                <h2 className="hive-section-title" style={{ marginBottom: 12 }}>Notifications</h2>
              )}
              <div className="hive-invite-list">
                {notifications.map((a) => {
                  const ident = a.identifier || {};
                  const isInvite = a.type === 'SPACE_INVITE' && ident.spaceId;
                  const vis = alertVisual(a.classification);
                  const busy = busyId === a.alertId || busyId === ident.spaceId;
                  return (
                    <div key={a.alertId} className="hive-invite-card">
                      <span className="hive-space-ic" style={{ background: vis.color }}>
                        <Icon name={vis.icon} size={18} color="#fff" />
                      </span>
                      <div className="hive-invite-body">
                        <span className="hive-card-title">{a.alertMessage}</span>
                        {a.timestamp && <span className="hive-meta">{a.timestamp}</span>}
                      </div>
                      <div className="hive-invite-actions">
                        {isInvite ? (
                          <>
                            <button
                              type="button"
                              className="hive-invite-accept hive-tap"
                              disabled={busy}
                              onClick={() => respondInvite(ident.spaceId, true)}
                            >
                              Accept
                            </button>
                            <button
                              type="button"
                              className="hive-invite-decline hive-tap"
                              disabled={busy}
                              onClick={() => respondInvite(ident.spaceId, false)}
                            >
                              Decline
                            </button>
                          </>
                        ) : (
                          <button
                            type="button"
                            className="hive-invite-decline hive-tap"
                            disabled={busy}
                            onClick={() => dismiss(a.alertId)}
                            aria-label="Dismiss notification"
                          >
                            Dismiss
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}

export default connectUser(UpdatesScreen);
